import { useState, useRef, useEffect } from "react";
import MediaProgressBar from "./media-progress-bar";
import MediaControls from "./media-controls";
import { useAudienceStore } from "@/lib/stores/audienceStore";

interface MediaPlayerProps {
  src: string;
  onPlayStateChange?: (isPlaying: boolean, hasStartedPlaying: boolean) => void;
  onEnded?: () => void;
  autoPlay?: boolean;
  disablePlaybackControls?: boolean;
  segment?: { start: number; end: number };
  onTimeUpdate?: (currentTime: number) => void;
  seekToTime?: number;
  isPaused?: boolean;
  shouldStop?: boolean; // New stop prop
  hideSkipControls?: boolean;
  disableProgressBarSeeking?: boolean;
  // Color customization props
  buttonColor?: string;
  playButtonColor?: string;
  progressBarBg?: string;
  timeTextColor?: string;
}

const MediaPlayer = ({
  src,
  onPlayStateChange,
  onEnded,
  autoPlay = false,
  disablePlaybackControls = false,
  segment,
  onTimeUpdate,
  seekToTime,
  isPaused = false,
  shouldStop = false, // New stop prop
  hideSkipControls = false,
  disableProgressBarSeeking = false,
  buttonColor,
  playButtonColor,
  progressBarBg,
  timeTextColor,
}: MediaPlayerProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const audience = useAudienceStore((state) => state.audience);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [actualDuration, setActualDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false);
  const previousSegmentRef = useRef<MediaPlayerProps["segment"] | null>(null);
  const hasEndedRef = useRef(false);

  const effectiveStart = segment?.start || 0;
  const effectiveEnd = segment?.end || actualDuration;
  const effectiveDuration = effectiveEnd - effectiveStart;
  const effectiveCurrentTime = currentTime - effectiveStart;

  // Unified progress calculation
  const progressPercentage =
    effectiveDuration > 0
      ? (effectiveCurrentTime / effectiveDuration) * 100
      : 0;

  // Handle stop prop (reset audio state)
  useEffect(() => {
    if (shouldStop) {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        setIsPlaying(false);
        setCurrentTime(audio.currentTime);
        setHasStartedPlaying(false);
        onPlayStateChange?.(false, false);
      }
    }
  }, [shouldStop, onPlayStateChange]);

  // Handle pause state (only pause if playing)
  useEffect(() => {
    const audio = audioRef.current;
    if (audio && isPaused && isPlaying) {
      audio.pause();
    }
  }, [isPaused, isPlaying]);

  useEffect(() => {
    if (seekToTime !== undefined && audioRef.current && !isDragging) {
      const audio = audioRef.current;
      if (Math.abs(audio.currentTime - seekToTime) > 0.5) {
        audio.currentTime = seekToTime;
      }
    }
  }, [seekToTime, isDragging]);

  // Format time helper
  const formatTime = (time: number) => {
    if (isNaN(time) || !isFinite(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  //Auto-play on mount if autoPlay is true
  useEffect(() => {
    const audio = audioRef.current;
    if (
      autoPlay &&
      audio &&
      src &&
      !hasStartedPlaying &&
      !isPaused &&
      !shouldStop
    ) {
      const timer = setTimeout(() => {
        audio.currentTime = effectiveStart;
        audio.play().catch(console.error);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [autoPlay, src, hasStartedPlaying, isPaused, shouldStop, effectiveStart]);

  // Auto-play when segment changes if autoPlay is true
  useEffect(() => {
    const audio = audioRef.current;
    if (!autoPlay || !audio || !segment || isPaused || shouldStop) return;

    const segmentChanged =
      previousSegmentRef.current?.start !== segment.start ||
      previousSegmentRef.current?.end !== segment.end;

    previousSegmentRef.current = segment;

    const needsSeek =
      segmentChanged ||
      audio.currentTime < segment.start ||
      audio.currentTime >= segment.end;

    const timer = setTimeout(() => {
      const currentAudio = audioRef.current;
      if (!currentAudio) return;

      if (needsSeek) {
        currentAudio.currentTime = segment.start;
      }

      if (currentAudio.paused) {
        currentAudio.play().catch(console.error);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [segment, autoPlay, isPaused, shouldStop]);

  // Reset player state when src changes
  useEffect(() => {
    setIsPlaying(false);
    setHasStartedPlaying(false);
    setCurrentTime(effectiveStart);
    setActualDuration(0);
    hasEndedRef.current = false;

    // Pause any playing audio when src changes and reset position
    // to avoid iOS/Safari restoring a cached playback position
    const audio = audioRef.current;
    if (audio) {
      if (!audio.paused) audio.pause();
      audio.currentTime = effectiveStart;
    }
  }, [src, effectiveStart]);

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !src) return;

    const handleLoadStart = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);
    const handleLoadedMetadata = () => {
      setActualDuration(audio.duration);
      setIsLoading(false);

      // Always reset to segment start to clear any browser-cached position (e.g. iOS Safari)
      audio.currentTime = effectiveStart;
      setCurrentTime(effectiveStart);
    };
    const handleTimeUpdate = () => {
      if (!isDragging) {
        // check if we've reached the segment end
        if (segment?.end && audio.currentTime >= effectiveEnd) {
          audio.pause();
          const reportedTime = Math.min(
            effectiveEnd,
            Number.isFinite(audio.duration) ? audio.duration : effectiveEnd,
          );
          setCurrentTime(reportedTime);
          // notify parent of the exact endtime
          onTimeUpdate?.(reportedTime);

          const isTrackEnd =
            Number.isFinite(audio.duration) &&
            Math.abs((audio.duration ?? 0) - effectiveEnd) < 0.3;

          if (isTrackEnd && !hasEndedRef.current) {
            hasEndedRef.current = true;
            onEnded?.();
          }
          // if autoPlay is off, reset
          if (!autoPlay) {
            audio.currentTime = effectiveStart;
            setCurrentTime(effectiveStart);
          }
          return;
        }
        setCurrentTime(audio.currentTime);
        onTimeUpdate?.(audio.currentTime);
      }
    };
    const handlePlay = () => {
      hasEndedRef.current = false;
      setIsPlaying(true);
      setHasStartedPlaying(true);
      onPlayStateChange?.(true, true);
    };
    const handlePause = () => {
      setIsPlaying(false);
      onPlayStateChange?.(false, hasStartedPlaying);
    };
    const handleEnded = () => {
      if (hasEndedRef.current) return;
      hasEndedRef.current = true;
      setIsPlaying(false);
      onPlayStateChange?.(false, hasStartedPlaying);
      onEnded?.();
    };
    const handleError = () => {
      setIsLoading(false);
      setIsPlaying(false);
      onPlayStateChange?.(false, hasStartedPlaying);
    };

    audio.addEventListener("loadstart", handleLoadStart);
    audio.addEventListener("canplay", handleCanPlay);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleError);

    // Set initial volume and playback rate
    audio.volume = volume;
    audio.playbackRate = playbackRate;
    audio.muted = volume === 0;

    return () => {
      audio.removeEventListener("loadstart", handleLoadStart);
      audio.removeEventListener("canplay", handleCanPlay);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
    };
  }, [
    src,
    volume,
    playbackRate,
    isDragging,
    onPlayStateChange,
    onEnded,
    hasStartedPlaying,
    autoPlay,
    segment,
    onTimeUpdate,
    effectiveStart,
    effectiveEnd,
  ]);

  // Play/Pause functionality
  const togglePlayPause = async () => {
    const audio = audioRef.current;
    if (!audio || !src || isPaused || shouldStop) return;

    try {
      if (isPlaying) {
        await audio.pause();
      } else {
        setIsLoading(true);
        if (
          audio.currentTime < effectiveStart ||
          audio.currentTime >= effectiveEnd
        ) {
          audio.currentTime = effectiveStart;
        }
        await audio.play();
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Audio playback error:", error);
      setIsLoading(false);
      setIsPlaying(false);
      onPlayStateChange?.(false, hasStartedPlaying);
    }
  };

  // Volume toggle
  const toggleVolume = () => {
    const newVolume = volume > 0 ? 0 : 1;
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
      // iOS Safari ignores programmatic volume changes but respects muted
      audioRef.current.muted = newVolume === 0;
    }
  };

  // Skip functions
  const skipBackward = () => {
    const audio = audioRef.current;
    if (!audio) return;
    const newTime = Math.max(effectiveStart, audio.currentTime - 10);
    audio.currentTime = newTime;
  };

  const skipForward = () => {
    const audio = audioRef.current;
    if (!audio) return;
    const newTime = Math.min(effectiveEnd, audio.currentTime + 10);
    audio.currentTime = newTime;
  };

  // Playback rate toggle
  const togglePlaybackRate = () => {
    const rates = [1, 1.25, 1.5];
    const currentIndex = rates.indexOf(playbackRate);
    const nextRate = rates[(currentIndex + 1) % rates.length];
    setPlaybackRate(nextRate);
    if (audioRef.current) {
      audioRef.current.playbackRate = nextRate;
    }
  };

  return (
    <div className="w-full px-5 space-y-4">
      {/* Hidden Audio Element */}
      {src && (
        <audio
          ref={audioRef}
          src={src}
          preload="metadata"
          muted={volume === 0}
        />
      )}

      {/* Progress Bar */}
      <MediaProgressBar
        currentTime={effectiveCurrentTime}
        duration={effectiveDuration}
        progressPercentage={progressPercentage}
        disableSeeking={disableProgressBarSeeking}
        onSeek={(newTime) => {
          const audio = audioRef.current;
          if (audio) {
            const absoluteTime = effectiveStart + newTime;
            audio.currentTime = absoluteTime;
            setCurrentTime(absoluteTime);
          }
        }}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={() => setIsDragging(false)}
        formatTime={formatTime}
        progressBarBg={progressBarBg}
        progressFillBg={audience === "CHILD" ? "bg-primary" : "bg-white"}
        timeTextColor={timeTextColor}
      />

      {/* Controls */}
      <MediaControls
        isPlaying={isPlaying}
        isLoading={isLoading}
        volume={volume}
        playbackRate={playbackRate}
        controlsDisabled={!src || disablePlaybackControls || isPaused || shouldStop}
        hideSkipControls={hideSkipControls}
        onPlayPause={togglePlayPause}
        onVolumeToggle={toggleVolume}
        onSkipBackward={skipBackward}
        onSkipForward={skipForward}
        onPlaybackRateChange={togglePlaybackRate}
        buttonColor={buttonColor}
        playButtonColor={playButtonColor}
      />

      {/* No Audio Source Message */}
      {!src && (
        <div className="text-center text-sm text-gray mt-2">
          No audio source provided
        </div>
      )}
    </div>
  );
};

export default MediaPlayer;
